/**
 * FE mirror of the backend's canonical firm-staff capability tiers
 * (backend/src/lib/tenant-roles.ts). 2026-07-19 miss-hunt P1
 * (state-code-firm-01): the FE admitted every firm-staff role to every
 * /admin surface and fired every admin query unconditionally, so an invited
 * `rm` or `director` landed on permanent red "Couldn't load" cards and
 * dead-end nav items for what are perfectly legitimate backend role
 * boundaries. These predicates let nav items and queries follow the SAME
 * tiers the backend enforces — the backend remains the real authz boundary.
 *
 * ── 4 OCTOBER 2026: THE TIERS THAT WERE MISSING ─────────────────────────────
 *
 * This file mirrored three backend tiers. Screens that needed the others
 * hand-rolled their own: `role === 'admin' || role === 'super_admin'` for the
 * owner-admin tier (dropping `director`), `role !== 'rm'` for the whole-book
 * tier, and nothing at all for branding, client onboarding or offboarding. The
 * QA persona kit's first headless run caught the result: an invited `rm` on
 * /admin/client-book drew three 403s (GET /v1/admin/branding twice, GET
 * /v1/firm/book-summary) and was offered "Add new client" and "Bulk add" for a
 * POST /v1/firm/clients that answers the firm-admin tier only.
 *
 * So every tenant tier a firm screen needs is mirrored here now, under the
 * backend's own names, and `firm-capabilities.test.ts` reads tenant-roles.ts
 * and FAILS when a set below stops matching the backend set it mirrors. The
 * "keep in lockstep" instruction this header used to carry is now a test.
 *
 * CONSOLE-ROLE SPACE. `superadmin` folds into `super_admin` (normalizeRole),
 * and the backend's act-as `firm-reader` is minted for a CLIENT tenant and is
 * never the role of a firm console session, so neither appears below.
 */

import { normalizeRole, type Role } from './route-policy';

/** isTenantWideReader: the whole book. NOT `rm`, which is scoped to its
 *  assigned clients. Book summary, document coverage, firm trusts list. */
const TENANT_WIDE_READER: ReadonlySet<Role> = new Set<Role>([
  'admin',
  'compliance',
  'director',
  'super_admin',
]);

/** isTenantWideWriter: writes across the book. Offboarding a client. */
const TENANT_WIDE_WRITER: ReadonlySet<Role> = new Set<Role>([
  'admin',
  'compliance',
  'director',
  'super_admin',
]);

/** isTenantAdmin: owner-admin. Taking on clients (POST /v1/firm/clients and
 *  bulk add), assigning relationship managers, who pays, billing and branding
 *  WRITES. Not compliance (a data writer, not an owner-admin), not `rm`. */
const TENANT_ADMIN: ReadonlySet<Role> = new Set<Role>([
  'admin',
  'director',
  'super_admin',
]);

/** isTenantAdminOrCompliance: DSAR, master lodgements, entity graph, audit
 *  export, BO PII, and branding and billing READ (rm EXCLUDED; director
 *  admitted per Paul's 2026-07-17 ruling). */
const ADMIN_OR_COMPLIANCE: ReadonlySet<Role> = new Set<Role>([
  'admin',
  'compliance',
  'director',
  'super_admin',
]);

/** isTenantWorklistParticipant: risk-review, BO lodgement-attempts, EDD worker
 *  (rm admitted, scoped; director EXCLUDED, as the backend has it). */
const WORKLIST: ReadonlySet<Role> = new Set<Role>([
  'admin',
  'compliance',
  'rm',
  'super_admin',
]);

/** Mirrors the backend `isTenantManager` (tenant-roles.ts): admin, director and
 *  the two platform-operator roles. Deliberately NARROWER than the tiers above
 *  — `compliance` is not admitted, because the backend does not admit it on the
 *  route this gates. Recording a FICA check is the firm putting its name to a
 *  statutory file, so the control is hidden from a chair that would be refused
 *  it rather than offered and then 403'd. */
const TENANT_MANAGER: ReadonlySet<Role> = new Set<Role>([
  'admin',
  'director',
  'super_admin',
]);

/** isPlatformSuperAdmin: Elreign's own operators. Recovery requests. */
const PLATFORM_SUPER_ADMIN: ReadonlySet<Role> = new Set<Role>(['super_admin']);

/** isLegalHoldViewer: the legal-holds register (the backend set also carries
 *  the `io` and `audit-witness` claims, which no console session holds). */
const LEGAL_HOLD_VIEWER: ReadonlySet<Role> = new Set<Role>([
  'admin',
  'compliance',
  'super_admin',
]);

/** FIC_AUTHORISED_ROLES: suspicious-transaction reports to the FIC (the
 *  backend set also carries the `io`, `mlro` and `audit-witness` claims). */
const FIC_AUTHORISED: ReadonlySet<Role> = new Set<Role>([
  'admin',
  'compliance',
  'super_admin',
]);

/** Every tier above, by its backend name, for the parity test. Not for
 *  screens: they call the named predicates, which say what they are for. */
export const FIRM_CAPABILITY_SETS = {
  tenantWideReader: TENANT_WIDE_READER,
  tenantWideWriter: TENANT_WIDE_WRITER,
  tenantAdmin: TENANT_ADMIN,
  tenantAdminOrCompliance: ADMIN_OR_COMPLIANCE,
  tenantWorklist: WORKLIST,
  tenantManager: TENANT_MANAGER,
  platformSuperAdmin: PLATFORM_SUPER_ADMIN,
  legalHoldViewer: LEGAL_HOLD_VIEWER,
  ficAuthorised: FIC_AUTHORISED,
} as const;

function has(set: ReadonlySet<Role>, role: string | null | undefined): boolean {
  if (!role) return false;
  return set.has(normalizeRole(role));
}

/** DSAR queue / Master ICMS / Entity graph / audit-export tier. */
export function canUseAdminOrComplianceTier(role: string | null | undefined): boolean {
  return has(ADMIN_OR_COMPLIANCE, role);
}

/** GET /v1/admin/branding and the billing read: the same backend tier as
 *  `canUseAdminOrComplianceTier`, named for what a screen uses it for. */
export function canReadFirmSettings(role: string | null | undefined): boolean {
  return has(ADMIN_OR_COMPLIANCE, role);
}

/** Risk-review queue / BO lodgement-attempts / EDD-worker tier. */
export function canUseWorklistTier(role: string | null | undefined): boolean {
  return has(WORKLIST, role);
}

/** The whole book: GET /v1/firm/book-summary, /v1/firm/document-coverage,
 *  /v1/firm/trusts. A scoped `rm` reads its own clients through the routes
 *  that scope for it (/v1/firm/clients, /v1/trusts, /v1/admin/risk-review). */
export function canReadWholeBook(role: string | null | undefined): boolean {
  return has(TENANT_WIDE_READER, role);
}

/** Writes across the book: offboarding a client (POST .../resign). */
export function canWriteWholeBook(role: string | null | undefined): boolean {
  return has(TENANT_WIDE_WRITER, role);
}

/** Owner-admin over the firm's clients: take a client on (one at a time, in
 *  bulk, or by inviting them to appoint the firm), assign relationship
 *  managers, and say who pays. */
export function canManageFirmClients(role: string | null | undefined): boolean {
  return has(TENANT_ADMIN, role);
}

/** Manual FICA capture (RULING 2026-09-08, Paul). */
export function canRecordFicaCheck(role: string | null | undefined): boolean {
  return has(TENANT_MANAGER, role);
}

/** GET /v1/audit/export (handlers/audit-export.ts): `isTenantAdminOrCompliance`
 *  after a step-up. The audit LOG is open to every member; its export is not. */
export function canExportAuditLog(role: string | null | undefined): boolean {
  return has(ADMIN_OR_COMPLIANCE, role);
}

/** POST /v1/team/invite (handlers/team-invite.ts:271) and POST
 *  /v1/team/{id}/remove (functions/auth/team-remove.ts:161): `isTenantManager`.
 *  Not compliance (Paul, 2026-07-17: an invite can mint any role, including
 *  admin) and not `rm`. A non-manager reaches team-invite only on the delegated
 *  TRUSTEE path (an end-client inviting a co-member to their own trust), which
 *  is never a staff invite. */
export function canManageStaff(role: string | null | undefined): boolean {
  return has(TENANT_MANAGER, role);
}

/** POST /v1/admin/team/{id}/invite-send (functions/auth/admin-team-invite-send.ts
 *  SEND_ROLES at :236, checked at :362 and :491): the owner-admin tier for a
 *  staff member's invitation (the only other caller is a 'user' trustee). */
export function canSendStaffInvite(role: string | null | undefined): boolean {
  return has(TENANT_ADMIN, role);
}

/** Grants to external auditors: the owner-admin tier, as on the server. */
export function canManageExternalAuditors(role: string | null | undefined): boolean {
  return has(TENANT_ADMIN, role);
}

/** Reports to the Financial Intelligence Centre. */
export function canFileFicReports(role: string | null | undefined): boolean {
  return has(FIC_AUTHORISED, role);
}

/** The legal-holds register. */
export function canViewLegalHolds(role: string | null | undefined): boolean {
  return has(LEGAL_HOLD_VIEWER, role);
}

/** Elreign's own platform operators. */
export function isPlatformOperator(role: string | null | undefined): boolean {
  return has(PLATFORM_SUPER_ADMIN, role);
}
